import { ArrowDown, ArrowUp } from 'lucide-react';
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';

import type { FundFitAITokenBreakdown } from '@/types/service/internalAnalytics';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { formatCurrency, formatNumber } from '@/utils/saasDashboardHelpers';

interface FundFitAITokenBreakdownChartProps {
  data: FundFitAITokenBreakdown;
}

const COLORS = {
  input: '#3b82f6',
  output: '#10b981',
};

const FundFitAITokenBreakdownChart = ({ data }: FundFitAITokenBreakdownChartProps) => {
  const chartData = [
    {
      name: 'Input',
      value: data.total_input_tokens,
      percentage: parseFloat(data.input_percentage),
      cost: parseFloat(data.total_input_cost_usd),
      color: COLORS.input,
    },
    {
      name: 'Output',
      value: data.total_output_tokens,
      percentage: parseFloat(data.output_percentage),
      cost: parseFloat(data.total_output_cost_usd),
      color: COLORS.output,
    },
  ];

  const totalTokens = data.total_input_tokens + data.total_output_tokens;
  const totalCost = parseFloat(data.total_input_cost_usd) + parseFloat(data.total_output_cost_usd);

  if (totalTokens === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='text-lg'>Token Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='h-[200px] flex items-center justify-center text-muted-foreground'>
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-lg'>Token Breakdown (Input vs Output)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className='flex flex-col md:flex-row items-center gap-6'>
          <div className='relative'>
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie
                  data={chartData}
                  cx='50%'
                  cy='50%'
                  innerRadius={50}
                  outerRadius={70}
                  paddingAngle={2}
                  dataKey='value'
                  nameKey='name'
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <span className='text-xs text-muted-foreground'>Total</span>
              <span className='text-sm font-bold'>{formatNumber(totalTokens)}</span>
            </div>
          </div>

          <div className='flex-1 grid grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <ArrowUp className='h-4 w-4 text-blue-500' />
                <span className='text-sm font-medium'>Input Tokens</span>
              </div>
              <p className='text-2xl font-bold' style={{ color: COLORS.input }}>
                {formatNumber(data.total_input_tokens)}
              </p>
              <p className='text-sm text-muted-foreground'>{data.input_percentage}% of total</p>
              <p className='text-sm'>{formatCurrency(parseFloat(data.total_input_cost_usd))}</p>
            </div>

            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <ArrowDown className='h-4 w-4 text-emerald-500' />
                <span className='text-sm font-medium'>Output Tokens</span>
              </div>
              <p className='text-2xl font-bold' style={{ color: COLORS.output }}>
                {formatNumber(data.total_output_tokens)}
              </p>
              <p className='text-sm text-muted-foreground'>{data.output_percentage}% of total</p>
              <p className='text-sm'>{formatCurrency(parseFloat(data.total_output_cost_usd))}</p>
            </div>
          </div>
        </div>

        <div className='mt-4 pt-4 border-t'>
          <div className='flex justify-between items-center'>
            <span className='text-sm text-muted-foreground'>Total Cost</span>
            <span className='text-lg font-bold'>{formatCurrency(totalCost)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FundFitAITokenBreakdownChart;
